import React, { useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import { useDimensionsRef } from 'hooks/useDimensionsRef/useDimensionsRef';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import { useInfiniteScroll } from 'pages/ReportsPage/components/AnalyticsDashboards/useInfiniteScroll';
import { REPORTS_PAGE_COLORS } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import {
  ChartScrollContainer,
  ChartLoadMoreRow,
  ChartScrollSentinel,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';

const BAR_HEIGHT = 22.5;
const BAR_GAP = 8;
const MARGIN_TOP = 10;
const MARGIN_BOTTOM = 20;
const LABEL_CHAR_WIDTH = 9;
const LABEL_PADDING = 16;
const LABEL_LINE_HEIGHT = 16;

export interface HorizontalBarItem {
  label: string;
  value: number;
  valueLabel: string;
  ariaLabel: string;
  tooltipHtml: string;
  barColor: string;
  valueLabelColor?: string;
}

interface HorizontalBarChartProps {
  items: HorizontalBarItem[];
  maxValue?: number;
  rightMargin?: number;
  leftMargin?: number;
  ariaLabel: string;
  showTrack?: boolean;
  wrapLabels?: boolean;
  scrollable?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

interface BarRow {
  item: HorizontalBarItem;
  lines: string[];
  barY: number;
  barHeight: number;
  centerY: number;
}

function estimateLeftMargin(labels: string[]): number {
  const longest = labels.reduce((a, b) => (a.length > b.length ? a : b), '');
  return Math.max(60, longest.length * LABEL_CHAR_WIDTH + LABEL_PADDING);
}

function wrapLabel(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text];
  const lines: string[] = [];
  let current = '';
  const commit = (): void => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };
  words.forEach((word) => {
    let remaining = word;
    while (remaining.length > maxChars) {
      commit();
      lines.push(remaining.slice(0, maxChars));
      remaining = remaining.slice(maxChars);
    }
    if (!remaining) return;
    if (!current) {
      current = remaining;
    } else if (current.length + 1 + remaining.length <= maxChars) {
      current = `${current} ${remaining}`;
    } else {
      commit();
      current = remaining;
    }
  });
  commit();
  return lines.length > 0 ? lines : [text];
}

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  items,
  maxValue,
  rightMargin = 140,
  leftMargin,
  ariaLabel,
  showTrack = true,
  wrapLabels = false,
  scrollable = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) => {
  const [containerRef, { width: containerWidth }] = useDimensionsRef<HTMLDivElement>();
  const tooltip = useChartTooltip();
  const [hovered, setHovered] = useState<number | null>(null);
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const infiniteScrollEnabled = scrollable && !!onLoadMore;

  useInfiniteScroll({
    rootRef: scrollRootRef,
    sentinelRef,
    hasMore: infiniteScrollEnabled && hasMore,
    isLoading: isLoadingMore,
    onLoadMore: onLoadMore ?? (() => undefined),
  });

  const marginLeft = useMemo(
    () => leftMargin ?? estimateLeftMargin(items.map((d) => d.label)),
    [items, leftMargin],
  );

  const { rows, height } = useMemo(() => {
    if (wrapLabels) {
      const maxChars = Math.max(6, Math.floor((marginLeft - LABEL_PADDING) / LABEL_CHAR_WIDTH));
      let cursor = 0;
      const wrappedRows: BarRow[] = items.map((d) => {
        const lines = wrapLabel(d.label, maxChars);
        const contentHeight = Math.max(BAR_HEIGHT, lines.length * LABEL_LINE_HEIGHT);
        const top = cursor;
        cursor += contentHeight + BAR_GAP;
        return {
          item: d,
          lines,
          barY: top + (contentHeight - BAR_HEIGHT) / 2,
          barHeight: BAR_HEIGHT,
          centerY: top + contentHeight / 2,
        };
      });
      return { rows: wrappedRows, height: MARGIN_TOP + MARGIN_BOTTOM + cursor };
    }

    const totalHeight = MARGIN_TOP + MARGIN_BOTTOM + items.length * (BAR_HEIGHT + BAR_GAP);
    const innerHeight = totalHeight - MARGIN_TOP - MARGIN_BOTTOM;
    const band = d3
      .scaleBand<number>()
      .domain(d3.range(items.length))
      .range([0, innerHeight])
      .padding(0.2);
    const uniformRows: BarRow[] = items.map((d, index) => {
      const barY = band(index) || 0;
      return {
        item: d,
        lines: [d.label],
        barY,
        barHeight: band.bandwidth(),
        centerY: barY + band.bandwidth() / 2,
      };
    });
    return { rows: uniformRows, height: totalHeight };
  }, [items, wrapLabels, marginLeft]);

  const innerWidth = Math.max(containerWidth - marginLeft - rightMargin, 0);

  const xScale = useMemo(() => {
    const domainMax = maxValue ?? (d3.max(items, (d) => d.value) || 1) * 1.1;
    return d3.scaleLinear().domain([0, domainMax]).range([0, innerWidth]);
  }, [items, maxValue, innerWidth]);

  const withScrollContainer = (chart: React.ReactElement) => {
    if (!scrollable) return chart;
    return (
      <ChartScrollContainer ref={scrollRootRef}>
        {chart}
        {infiniteScrollEnabled && <ChartScrollSentinel ref={sentinelRef} aria-hidden />}
        {isLoadingMore && (
          <ChartLoadMoreRow>
            <BKJLoadingSpinner variant="Default" />
          </ChartLoadMoreRow>
        )}
      </ChartScrollContainer>
    );
  };

  if (!containerWidth || items.length === 0) {
    return withScrollContainer(
      <div
        ref={containerRef}
        style={{
          width: '100%',
          minHeight: 160,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      />,
    );
  }

  return withScrollContainer(
    <div
      ref={containerRef}
      style={{
        width: '100%',
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <svg width={containerWidth} height={height} aria-label={ariaLabel}>
        <g transform={`translate(${marginLeft},${MARGIN_TOP})`}>
          {rows.map(({ item: d, lines, barY, barHeight, centerY }, index) => (
            <g key={index}>
              {showTrack && (
                <rect
                  x={0}
                  y={barY}
                  width={innerWidth}
                  height={barHeight}
                  fill={REPORTS_PAGE_COLORS.lightenFallback}
                  rx={3}
                />
              )}
              <rect
                x={0}
                y={barY}
                width={xScale(d.value)}
                height={barHeight}
                fill={d.barColor}
                rx={3}
                role="img"
                aria-label={d.ariaLabel}
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                opacity={hovered === index ? 0.8 : 1}
                onMouseEnter={(e) => {
                  setHovered(index);
                  tooltip.show(e, d.tooltipHtml);
                }}
                onMouseMove={(e) => tooltip.move(e)}
                onMouseLeave={() => {
                  setHovered(null);
                  tooltip.hide();
                }}
                onFocus={(e) => {
                  setHovered(index);
                  tooltip.show(e, d.tooltipHtml);
                }}
                onBlur={() => {
                  setHovered(null);
                  tooltip.hide();
                }}
              />
              <text
                x={-8}
                y={centerY}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="14px"
                fill={REPORTS_PAGE_COLORS.textPrimary}
              >
                {lines.length === 1
                  ? lines[0]
                  : lines.map((line, i) => (
                      <tspan
                        key={`${d.label}-${i}`}
                        x={-8}
                        y={centerY + (i - (lines.length - 1) / 2) * LABEL_LINE_HEIGHT}
                      >
                        {line}
                      </tspan>
                    ))}
              </text>
              <text
                x={innerWidth + rightMargin - 8}
                y={centerY}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12px"
                fill={d.valueLabelColor || REPORTS_PAGE_COLORS.textPrimary}
              >
                {d.valueLabel}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>,
  );
};
